import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useUserSensorReadingService } from "../../services/sensorForuser";
import { useNavigate } from "react-router-dom";
import { useBorewellCustomerService } from "../../services/userService";

function formatTs(ts) {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
}

function toNumber(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function metricUnit(metric) {
  switch (metric) {
    case "waterLevel":
      return "m";
    case "currentFlow":
      return "m3/h";
    case "ph":
      return "";
    case "tds":
      return "mg/L";
    case "pm25":
      return "µg/m³";
    case "pm10":
      return "µg/m³";
    default:
      return "";
  }
}

function buildHistorySeries(reading, key) {
  const points = [];

  const pushPoint = (doc) => {
    const ts =
      doc.readingTimestamp ||
      doc.updatedAt ||
      doc.createdAt ||
      reading.readingTimestamp ||
      reading.updatedAt ||
      reading.createdAt;
    const value = toNumber(doc[key]);
    const t = ts ? new Date(ts).getTime() : NaN;
    if (!Number.isFinite(t) || value === null) return;
    points.push({ t, ts: new Date(t).toISOString(), value });
  };

  if (Array.isArray(reading.readingHistory)) {
    for (const raw of reading.readingHistory) {
      try {
        const parsed = JSON.parse(raw);
        pushPoint(parsed);
      } catch {
        // ignore bad history row
      }
    }
  }

  pushPoint(reading);

  points.sort((a, b) => a.t - b.t);

  // Deduplicate by timestamp (keep last)
  const byT = new Map();
  for (const p of points) byT.set(p.t, p);

  return Array.from(byT.values()).sort((a, b) => a.t - b.t);
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { getUserSensorReadings } = useUserSensorReadingService();
  const { getBorewellCustomersByUser } = useBorewellCustomerService();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState(null);

  const [selectedSensorKey, setSelectedSensorKey] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("waterLevel");

  useEffect(() => {
    let mounted = true;
    const loadDevices = async () => {
      try {
        setDevicesLoading(true);
        setDevicesError(null);
        const res = await getBorewellCustomersByUser();
        if (!mounted) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        // Deduplicate by `serialNo` (your `sensorId`) to avoid duplicates counting as extra sensors.
        const bySensorId = new Map();
        for (const c of list) {
          const sensorId = c?.serialNo;
          if (!sensorId) continue;
          if (!bySensorId.has(sensorId)) {
            bySensorId.set(sensorId, {
              key: `${c.monitoringUnitId}::${c.serialNo}`,
              monitoringUnitId: c.monitoringUnitId,
              sensorId: c.serialNo,
              serialNo: c.serialNo,
              sensorName: c.sensorName?.name || c.sensorName?.type || "",
            });
          }
        }
        setDevices(Array.from(bySensorId.values()));
      } catch (e) {
        // If device fetch fails, we still can show readings-based UI
        if (!mounted) return;
        setDevices([]);
        setDevicesError("Failed to load assigned devices.");
      } finally {
        if (mounted) setDevicesLoading(false);
      }
    };
    loadDevices();
    return () => {
      mounted = false;
    };
  }, [getBorewellCustomersByUser]);

  const getUserSensorReadingsRef = useRef(getUserSensorReadings);
  useEffect(() => {
    getUserSensorReadingsRef.current = getUserSensorReadings;
  }, [getUserSensorReadings]);

  useEffect(() => {
    let intervalId;

    const fetchReadings = async () => {
      try {
        setError(null);
        const response = await getUserSensorReadingsRef.current({
          historyLimit: 100,
          dailyFlowLimit: 30,
          onlyWithData: false,
        });
        setReadings(Array.isArray(response?.data) ? response.data : []);
      } catch (e) {
        setError("Failed to load sensor readings");
      } finally {
        setLoading(false);
      }
    };

    fetchReadings();
    intervalId = setInterval(fetchReadings, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const sensors = useMemo(() => {
    if (devices.length > 0) {
      return [...devices].sort((a, b) =>
        a.monitoringUnitId.localeCompare(b.monitoringUnitId)
      );
    }

    // Fallback: if device API fails/returns empty, derive from readings
    // so the dashboard still works.
    const map = new Map();
    for (const r of readings) {
      const key = `${r.monitoringUnitId}::${r.sensorId}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          monitoringUnitId: r.monitoringUnitId,
          sensorId: r.sensorId,
          sensorName: "",
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.monitoringUnitId.localeCompare(b.monitoringUnitId)
    );
  }, [devices, readings]);

  useEffect(() => {
    if (!selectedSensorKey && sensors.length > 0) {
      setSelectedSensorKey(sensors[0].key);
    }
  }, [sensors, selectedSensorKey]);

  const selectedReading = useMemo(() => {
    if (!selectedSensorKey) return null;
    const [monitoringUnitId, sensorId] = selectedSensorKey.split("::");
    return (
      readings.find(
        (r) => r.monitoringUnitId === monitoringUnitId && r.sensorId === sensorId
      ) || null
    );
  }, [readings, selectedSensorKey]);

  const timeSeries = useMemo(() => {
    if (!selectedReading) return [];
    return buildHistorySeries(selectedReading, selectedMetric).map((p) => ({
      tsLabel: formatTs(p.ts),
      value: p.value,
    }));
  }, [selectedReading, selectedMetric]);

  const dailyFlow = useMemo(() => {
    if (!selectedReading || !Array.isArray(selectedReading.dailyFlowData)) return [];
    return selectedReading.dailyFlowData
      .map((d) => ({
        date: d.date ? new Date(d.date).toLocaleDateString() : "",
        totalFlow: toNumber(d.totalFlow),
      }))
      .filter((d) => d.date && d.totalFlow !== null);
  }, [selectedReading]);

  const lastUpdate = useMemo(() => {
    const ts = readings
      .map((r) => r.updatedAt || r.readingTimestamp || r.createdAt)
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((t) => Number.isFinite(t));
    if (ts.length === 0) return null;
    return new Date(Math.max(...ts)).toISOString();
  }, [readings]);

  const cards = useMemo(() => {
    const assigned = devices.length > 0 ? devices.length : sensors.length;
    const activeSet = new Set(
      readings.map((r) => `${r.monitoringUnitId}::${r.sensorId}`)
    );
    const activeCount = sensors.filter((s) => activeSet.has(s.key)).length;
    return [
      { title: "Assigned Sensors", value: assigned, unit: "devices" },
      { title: "Active Sensors", value: activeCount, unit: "devices" },
      { title: "Critical Alerts", value: 0, unit: "alerts" },
      { title: "Last Update", value: lastUpdate ? formatTs(lastUpdate) : "—", unit: "" },
    ];
  }, [sensors, readings, lastUpdate]);

  if (devicesLoading) {
    return <div className="p-6">Loading dashboard…</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">{error}</p>
          <p className="text-red-600 text-sm mt-1">
            Please check backend is running and you are logged in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stat.value}
                  <span className="text-sm font-normal text-gray-500 ml-1">
                    {stat.unit}
                  </span>
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 rounded bg-gray-200" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Graphs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Sensor Trends</h2>
            <p className="text-sm text-gray-500 mt-1">
              Showing your sensors 
            </p>
           
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sensor
              </label>
              <select
                className="w-full sm:w-72 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={selectedSensorKey}
                onChange={(e) => setSelectedSensorKey(e.target.value)}
              >
                {sensors.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.sensorName
                      ? `${s.sensorName} • ${s.sensorId}`
                      : `${s.monitoringUnitId} • ${s.sensorId}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Metric
              </label>
              <select
                className="w-full sm:w-52 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                <option value="waterLevel">Water Level (m)</option>
                <option value="currentFlow">Current Flow (m3/h)</option>
                <option value="ph">pH</option>
                <option value="tds">TDS (mg/L)</option>
                <option value="pm25">PM2.5</option>
                <option value="pm10">PM10</option>
              </select>
            </div>
          </div>
        </div>

        {!selectedReading ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-gray-600">
            No sensor selected.
          </div>
        ) : timeSeries.length < 2 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-gray-600">
            <div className="font-medium text-gray-700 mb-2">
              Not enough history yet for a trend chart.
            </div>
            <div className="text-sm">
              Latest {selectedMetric}:
              <span className="font-semibold text-gray-900 ml-2">
                {selectedReading[selectedMetric] ?? "—"} {metricUnit(selectedMetric)}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Once multiple readings arrive, the trend graph will appear here.
            </div>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tsLabel" hide />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={selectedMetric}
                  stroke="#2563eb"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Daily Total Flow
          </h3>
          {dailyFlow.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-gray-600">
              No daily flow data available for this sensor.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyFlow}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="totalFlow"
                    name="Total Flow (m3)"
                    fill="#16a34a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Sensor Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">
          Latest Readings
        </h2>
        {sensors.length === 0 ? (
          <div className="text-gray-600">No devices found for your account.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sensors.slice(0, 6).map((d) => {
              const latest = readings.find(
                (r) =>
                  r.monitoringUnitId === d.monitoringUnitId &&
                  r.sensorId === d.sensorId
              );
              return (
              <button
                type="button"
                onClick={() =>
                  latest?._id && navigate(`/client/sensor-readings/${latest._id}`)
                }
                key={d.key}
                className="text-left bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {d.sensorName
                        ? `${d.sensorName} • ${d.sensorId}`
                        : `${d.monitoringUnitId} • ${d.sensorId}`}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Water Level: {latest?.waterLevel ?? "—"} m · Current Flow:{" "}
                      {latest?.currentFlow ?? "—"} m3/h · pH: {latest?.ph ?? "—"}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap">
                    {latest
                      ? formatTs(
                          latest.updatedAt || latest.readingTimestamp || latest.createdAt
                        )
                      : "No data yet"}
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
